// src/utils/propagateTasks.ts
// Gap 1 — Drawing task → ConfiguredTask auto-bridge
//
// When a PM clicks "Push to mobile" on an APPROVED DrawingTask, this utility
// reads the DrawingTask fields + linked BOM lines and produces a draft
// ConfiguredTask that the PM reviews (and optionally edits) before final push.
//
// The draft is stored in React state in Drawings.tsx until the PM confirms.
// commitDraftTask() writes it into MOCK_CONFIGURED_TASKS (the live store).
// ─────────────────────────────────────────────────────────────────

import { DrawingTask, BOMItem, MOCK_BOM } from './mockDataDrawings'
import { ConfiguredTask, MaterialRequirement } from './mockDataMobile'
import { MOCK_JOBS } from './mockData'

// ── Draft type — ConfiguredTask without a final id, with _isDraft flag ────
export type PropagatedTaskDraft = Omit<ConfiguredTask, 'id'> & {
  drawingTaskId: string        // FK back to source DrawingTask
  _isDraft: true
  _resolvedWIId: string | null // WI that was auto-resolved (or null if not found)
}

// ── Discipline → photo timing mapping ─────────────────────────────
const DISCIPLINE_PHOTO_TIMING: Record<string, ConfiguredTask['photoTiming']> = {
  Civil:             'BEFORE_AFTER',
  Electrical:        'BEFORE_DURING_AFTER',
  HV:                'BEFORE_DURING_AFTER',
  'Traffic Control': 'BEFORE_AFTER',
  Commissioning:     'BEFORE_DURING_AFTER',
  Other:             'BEFORE_AFTER',
}

const DISCIPLINE_PHOTO_COUNT: Record<string, number> = {
  Civil:             2,
  Electrical:        3,
  HV:                4,
  'Traffic Control': 2,
  Commissioning:     3,
  Other:             2,
}

// ─────────────────────────────────────────────────────────────────
// buildDraftFromDrawingTask()
//
// Converts a single DrawingTask into a draft ConfiguredTask.
// The PM reviews this in PropagateTasksModal before committing.
// ─────────────────────────────────────────────────────────────────
export function buildDraftFromDrawingTask(
  dt: DrawingTask,
  allBomItems: BOMItem[] = MOCK_BOM,
  resolvedWIId: string | null = null,
): PropagatedTaskDraft {
  const job = MOCK_JOBS.find(j => j.id === dt.jobId)

  // ── Resolve BOM items (drawingTask.bomItemIds → BOM lines) ────
  const materialsRequired: MaterialRequirement[] = dt.bomItemIds
    .map(id => allBomItems.find(b => b.id === id))
    .filter((b): b is BOMItem => !!b)
    .map(b => ({
      bomLineId:   b.id,
      description: b.description,
      qty:         b.quantity,
      unit:        b.unit,
      stockStatus: (b.stockStatus as MaterialRequirement['stockStatus']) ?? 'UNKNOWN',
      specRef:     b.specRef ?? null,
    }))

  // ── Photo config from discipline + hold point ─────────────────
  const basePhotoTiming = DISCIPLINE_PHOTO_TIMING[dt.discipline] ?? 'BEFORE_AFTER'
  const photoTiming: ConfiguredTask['photoTiming'] = dt.holdPointRef
    ? 'BEFORE_DURING_AFTER'
    : basePhotoTiming

  const photoRequirements = dt.holdPointRef
    ? Math.max(DISCIPLINE_PHOTO_COUNT[dt.discipline] ?? 2, 3)
    : DISCIPLINE_PHOTO_COUNT[dt.discipline] ?? 2

  // ── Encode dotted taskNumber string as sortable int ───────────
  // e.g. '1.2.3' → pad each segment → '010203' → parseInt
  const taskNumberInt = dt.taskNumber
    .split('.')
    .map(s => s.padStart(2, '0'))
    .join('')
    .replace(/^0+/, '') || '0'

  return {
    _isDraft:         true,
    drawingTaskId:    dt.id,
    _resolvedWIId:    resolvedWIId,
    ...({ id: `ct-draft-${dt.id}` } as any),  // id added at commit time
    jobId:            dt.jobId,
    jobNumber:        job?.jobNumber ?? '',
    taskNumber:       parseInt(taskNumberInt, 10) || 1,
    title:            dt.title,
    description:      dt.description
                        ?? `Carry out work as specified. Refer to drawings and applicable specifications.`,
    specClauseRef:    dt.specClauseRef,
    photoRequirements,
    photoTiming,
    holdPointLinkage: dt.holdPointRef,
    sequenceDependency: dt.prerequisiteTaskIds.length > 0
                          ? dt.prerequisiteTaskIds[0]
                          : null,
    assignedToId:     dt.assignedToId,
    assignedToName:   dt.assignedToName,
    targetDate:       null,       // PM sets this in the review modal
    pushedToMobile:   false,      // becomes true on commit
    pushedAt:         null,
    discipline:       dt.discipline,
    materialsRequired,
  }
}

// ─────────────────────────────────────────────────────────────────
// commitDraftTask()
//
// Finalises a reviewed draft into a proper ConfiguredTask.
// Assigns a permanent id and marks pushedToMobile: true.
// Caller pushes the returned ConfiguredTask into MOCK_CONFIGURED_TASKS.
// ─────────────────────────────────────────────────────────────────
export function commitDraftTask(draft: PropagatedTaskDraft): ConfiguredTask {
  const { _isDraft, _resolvedWIId, drawingTaskId, ...rest } = draft
  return {
    ...rest,
    id:             `ct-${drawingTaskId}-${Date.now()}`,
    drawingTaskId,                   // preserved as FK
    pushedToMobile: true,
    pushedAt:       new Date().toISOString(),
  }
}

// ─────────────────────────────────────────────────────────────────
// buildDraftsForAll()
//
// Convenience: produce drafts for every APPROVED task in a list
// that has not yet been pushed. Used for bulk-push actions.
// ─────────────────────────────────────────────────────────────────
export function buildDraftsForAll(
  tasks: DrawingTask[],
  allBomItems: BOMItem[] = MOCK_BOM,
): PropagatedTaskDraft[] {
  return tasks
    .filter(t => t.status === 'APPROVED' && !t.publishedToMobile)
    .map(t => buildDraftFromDrawingTask(t, allBomItems))
}

// ─────────────────────────────────────────────────────────────────
// materialAvailabilityWarning()
//
// Returns materials that are NOT on site — used in PropagateTasksModal
// to warn the PM before pushing the task to crew devices.
// ─────────────────────────────────────────────────────────────────
export function materialAvailabilityWarning(draft: PropagatedTaskDraft): MaterialRequirement[] {
  return (draft.materialsRequired ?? []).filter(m => m.stockStatus !== 'ON_SITE')
}
